import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "wouter";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Heart, Pencil, X, Check, Archive, Trash2,
  Loader2, Sparkles, Shield, Crosshair, Flame,
  Quote, Target, Lightbulb, Zap, ChevronRight,
} from "lucide-react";
import type { NarrativeAngle } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { FAMILY_COLORS } from "./narratives";
import { NarrativeFunnelStageBadge } from "@/components/narrative-funnel-stage-badge";

const FAMILY_ACCENT_MAP: Record<string, string> = {
  conflict: "from-red-500/10 to-transparent border-red-500/20",
  identity: "from-violet-500/10 to-transparent border-violet-500/20",
  aspiration: "from-amber-500/10 to-transparent border-amber-500/20",
  transformation: "from-emerald-500/10 to-transparent border-emerald-500/20",
  belonging: "from-blue-500/10 to-transparent border-blue-500/20",
  discovery: "from-cyan-500/10 to-transparent border-cyan-500/20",
};

const FAMILY_ICON_MAP: Record<string, typeof Sparkles> = {
  conflict: Crosshair,
  identity: Sparkles,
  aspiration: Target,
  transformation: Zap,
  belonging: Shield,
  discovery: Lightbulb,
};

const SCORE_META = [
  { key: "resonanceScore", label: "Resonance", color: "bg-violet-500" },
  { key: "proofScore", label: "Proof", color: "bg-emerald-500" },
  { key: "distinctivenessScore", label: "Distinct", color: "bg-amber-500" },
  { key: "emotionalScore", label: "Emotional", color: "bg-rose-500" },
] as const;

export default function NarrativeAngleDetailPage() {
  const { brandId, angleId } = useParams<{ brandId: string; angleId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Record<string, any>>({});

  const { data: angle, isLoading } = useQuery<NarrativeAngle>({
    queryKey: [`/api/narrative-angles/${angleId}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PATCH", `/api/narrative-angles/${angleId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-angles/${angleId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      toast({ title: "Angle updated" });
      setEditing(false);
    },
    onError: (e: Error) => {
      toast({ title: "Update failed", description: e.message, variant: "destructive" });
    },
  });

  const favMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-angles/${angleId}/favorite`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/narrative-angles/${angleId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      if (angle?.generationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/narrative-generations/${angle.generationId}`] });
      }
    },
    onError: (e: Error) => {
      toast({ title: "Failed to toggle favorite", description: e.message, variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/narrative-angles/${angleId}/archive`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      toast({ title: "Angle archived" });
      navigate(`/brands/${brandId}/narratives`);
    },
    onError: (e: Error) => {
      toast({ title: "Archive failed", description: e.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/narrative-angles/${angleId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/narrative-angles`] });
      toast({ title: "Angle deleted" });
      navigate(`/brands/${brandId}/narratives`);
    },
    onError: (e: Error) => {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-8">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-10 w-80" />
        <Skeleton className="h-64 w-full rounded-2xl" />
        <div className="grid grid-cols-4 gap-4">
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
          <Skeleton className="h-20 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!angle) return <div className="p-8">Angle not found</div>;

  const proofAnchors = (angle.proofAnchorsJson as string[] | null) || [];
  const angleStems = (angle.angleStemsJson as string[] | null) || [];
  const family = angle.family || "conflict";
  const accentGradient = FAMILY_ACCENT_MAP[family] || FAMILY_ACCENT_MAP.conflict;
  const FamilyIcon = FAMILY_ICON_MAP[family] || Sparkles;

  function startEdit() {
    setEditData({
      name: angle!.name,
      summary: angle!.summary || "",
      coreTension: angle!.coreTension || "",
      personaRelevance: angle!.personaRelevance || "",
      productTruth: angle!.productTruth || "",
      beliefShift: angle!.beliefShift || "",
      promise: angle!.promise || "",
      proofAnchorsJson: proofAnchors,
      angleStemsJson: angleStems,
      notes: angle!.notes || "",
    });
    setEditing(true);
  }

  function saveEdit() {
    updateMutation.mutate(editData);
  }

  function updateField(field: string, value: any) {
    setEditData(prev => ({ ...prev, [field]: value }));
  }

  const hasScores = SCORE_META.some(s => (angle as any)[s.key] !== null && (angle as any)[s.key] !== undefined);
  const avgScore = hasScores
    ? Math.round(SCORE_META.reduce((sum, s) => sum + ((angle as any)[s.key] || 0), 0) / SCORE_META.filter(s => (angle as any)[s.key] != null).length * 10) / 10
    : null;

  return (
    <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-8 fade-in-up">

      <div className="flex items-center gap-2">
        <Link href={angle.generationId ? `/brands/${brandId}/narrative-generations/${angle.generationId}` : `/brands/${brandId}/narratives`}>
          <button className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="button-back">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        </Link>
        <ChevronRight className="h-3 w-3 text-muted-foreground/40" />
        <span className="text-xs text-muted-foreground truncate max-w-[200px]">{angle.name}</span>
      </div>

      <div className={`rounded-2xl border bg-gradient-to-br ${accentGradient} p-6 md:p-8`}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-background/80 flex items-center justify-center">
              <FamilyIcon className="h-4 w-4 text-foreground/70" />
            </div>
            <div>
              {angle.family && (
                <Badge className={`text-[10px] rounded-full px-2 py-0 ${FAMILY_COLORS[angle.family] || "bg-gray-100 text-gray-700"}`}>
                  {angle.family}
                </Badge>
              )}
              <NarrativeFunnelStageBadge stage={angle.funnelStage} />
              {angle.subtype && (
                <p className="text-[10px] text-muted-foreground mt-0.5">{angle.subtype.replace(/_/g, " ")}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => favMutation.mutate()}
              data-testid="button-favorite"
            >
              <Heart className={`h-4 w-4 ${angle.isFavorite ? "fill-red-500 text-red-500" : "text-muted-foreground"}`} />
            </Button>
            {!editing ? (
              <Button variant="ghost" size="sm" className="h-8 rounded-full text-xs gap-1.5" onClick={startEdit} data-testid="button-edit">
                <Pencil className="h-3 w-3" />
                Edit
              </Button>
            ) : (
              <>
                <Button variant="ghost" size="sm" className="h-8 rounded-full text-xs" onClick={() => setEditing(false)} data-testid="button-cancel-edit">
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
                <Button size="sm" className="h-8 rounded-full text-xs" onClick={saveEdit} disabled={updateMutation.isPending} data-testid="button-save-edit">
                  {updateMutation.isPending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                  Save
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 rounded-full text-xs text-muted-foreground hover:text-destructive"
              onClick={() => archiveMutation.mutate()}
              data-testid="button-archive"
            >
              <Archive className="h-3 w-3 mr-1" />
              Archive
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive"
              onClick={() => deleteMutation.mutate()}
              data-testid="button-delete"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {editing ? (
          <Input
            value={editData.name}
            onChange={(e) => updateField("name", e.target.value)}
            className="text-2xl font-bold bg-background/50 border-none mb-3"
            data-testid="input-name"
          />
        ) : (
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-3" data-testid="text-angle-name">{angle.name}</h1>
        )}

        {editing ? (
          <Textarea value={editData.summary} onChange={(e) => updateField("summary", e.target.value)} className="bg-background/50 border-none text-sm" data-testid="input-summary" />
        ) : angle.summary ? (
          <p className="text-[15px] text-foreground/60 leading-relaxed max-w-2xl" data-testid="text-summary">{angle.summary}</p>
        ) : null}

        {angle.editedByUser && (
          <Badge variant="secondary" className="text-[9px] rounded-full px-2 gap-1 mt-3">
            <Pencil className="h-2.5 w-2.5" />
            Edited
          </Badge>
        )}
      </div>

      {hasScores && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {SCORE_META.map(({ key, label, color }) => {
            const val = (angle as any)[key] as number | null;
            if (val == null) return null;
            return (
              <div key={key} className="rounded-xl border bg-card p-4 text-center" data-testid={`score-${key}`}>
                <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-2">{label}</p>
                <p className="text-2xl font-bold tabular-nums">{val}</p>
                <div className="h-1 bg-muted rounded-full mt-2.5 overflow-hidden">
                  <div className={`h-full rounded-full ${color}`} style={{ width: `${(val / 10) * 100}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {avgScore !== null && (
        <div className="flex items-center gap-3 px-1">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Avg Score: <span className="font-semibold text-foreground">{avgScore}</span></span>
          <div className="h-px flex-1 bg-border" />
        </div>
      )}

      <div className="space-y-1">
        {[
          { key: "coreTension", label: "Core Tension", icon: Crosshair, desc: "The fundamental conflict this angle addresses" },
          { key: "personaRelevance", label: "Persona Relevance", icon: Target, desc: "Why this matters to the target persona" },
          { key: "productTruth", label: "Product Truth", icon: Shield, desc: "The product reality that supports this angle" },
          { key: "beliefShift", label: "Belief Shift", icon: Zap, desc: "The change in worldview this narrative drives" },
          { key: "promise", label: "Promise", icon: Sparkles, desc: "What the audience gains from this narrative" },
        ].map(({ key, label, icon: FieldIcon, desc }) => {
          const val = (angle as any)[key] as string | null;
          if (!val && !editing) return null;
          return (
            <div key={key} className="group rounded-xl border border-transparent hover:border-border hover:bg-muted/30 transition-all p-4" data-testid={`section-${key}`}>
              <div className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-lg bg-muted/60 flex items-center justify-center shrink-0 mt-0.5">
                  <FieldIcon className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">{label}</p>
                  </div>
                  {editing ? (
                    <Textarea
                      value={editData[key]}
                      onChange={(e) => updateField(key, e.target.value)}
                      className="min-h-[60px] text-sm"
                      data-testid={`input-${key}`}
                    />
                  ) : (
                    <p className="text-[13px] text-foreground/80 leading-relaxed">{val}</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(proofAnchors.length > 0 || angleStems.length > 0 || editing) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(proofAnchors.length > 0 || editing) && (
            <Card className="rounded-xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Proof Anchors</p>
                </div>
                {editing ? (
                  <Textarea
                    value={(editData.proofAnchorsJson || []).join("\n")}
                    onChange={(e) => updateField("proofAnchorsJson", e.target.value.split("\n").filter(Boolean))}
                    placeholder="One per line"
                    className="min-h-[80px] font-mono text-xs"
                    data-testid="input-proof-anchors"
                  />
                ) : (
                  <ul className="space-y-2">
                    {proofAnchors.map((p, i) => (
                      <li key={i} className="text-[12px] text-foreground/70 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:h-[3px] before:w-[3px] before:rounded-full before:bg-emerald-500/50" data-testid={`text-proof-${i}`}>{p}</li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}

          {(angleStems.length > 0 || editing) && (
            <Card className="rounded-xl">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Quote className="h-3.5 w-3.5 text-muted-foreground" />
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">Angle Stems</p>
                </div>
                {editing ? (
                  <Textarea
                    value={(editData.angleStemsJson || []).join("\n")}
                    onChange={(e) => updateField("angleStemsJson", e.target.value.split("\n").filter(Boolean))}
                    placeholder="One per line"
                    className="min-h-[80px] font-mono text-xs"
                    data-testid="input-angle-stems"
                  />
                ) : (
                  <ul className="space-y-2">
                    {angleStems.map((s, i) => (
                      <li key={i} className="text-[12px] text-foreground/70 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[7px] before:h-[3px] before:w-[3px] before:rounded-full before:bg-violet-500/50" data-testid={`text-stem-${i}`}>
                        <span className="italic">{s}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {(angle.notes || editing) && (
        <div className="rounded-xl border border-dashed border-border/60 p-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60 mb-2">Notes</p>
          {editing ? (
            <Textarea value={editData.notes} onChange={(e) => updateField("notes", e.target.value)} className="min-h-[80px]" data-testid="input-notes" />
          ) : (
            <p className="text-[13px] text-foreground/50 leading-relaxed">{angle.notes}</p>
          )}
        </div>
      )}

    </div>
  );
}
